import type { ComponentType, CSSProperties } from 'react';
import type { IconProps } from '@phosphor-icons/react';
import { cx } from '../utils/cx.js';
import styles from './WpDuotoneIcon.module.css';

export type WpDuotoneIconTone = 'accent' | 'muted' | 'danger';

export type WpIconComponent = ComponentType<IconProps>;

export type WpDuotoneIconProps = {
  icon: WpIconComponent;
  tone?: WpDuotoneIconTone;
  weight?: IconProps['weight'];
  size?: IconProps['size'];
  className?: string;
};

const TONE: Record<WpDuotoneIconTone, { primary: string; secondary: string }> = {
  accent: {
    primary: 'var(--wp-brunswick-800)',
    secondary: 'color-mix(in srgb, var(--wp-brunswick-800) 28%, var(--wp-surface-card))',
  },
  muted: {
    primary: 'var(--wp-text-secondary)',
    secondary: 'color-mix(in srgb, var(--wp-text-secondary) 24%, var(--wp-surface-card))',
  },
  danger: {
    primary: 'var(--wp-danger-700, #a93226)',
    secondary: 'color-mix(in srgb, var(--wp-danger-500, #e74c3c) 30%, var(--wp-surface-card))',
  },
};

/** Phosphor icon — rounded regular weight by default; pass weight="duotone" for two-plane fill. */
export function WpDuotoneIcon({
  icon: Icon,
  size = 16,
  tone = 'accent',
  weight = 'regular',
  className,
  ...rest
}: WpDuotoneIconProps) {
  const colors = TONE[tone];
  return (
    <span
      className={cx(styles.root, className)}
      style={{ '--wp-icon-duotone-secondary': colors.secondary } as CSSProperties}
    >
      <Icon size={size} weight={weight} color={colors.primary} aria-hidden {...rest} />
    </span>
  );
}
